import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface GroupNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (name: string) => void;
  defaultName?: string;
}

export function GroupNameDialog({ 
  open, 
  onOpenChange, 
  onConfirm,
  defaultName = 'Group/Rack'
}: GroupNameDialogProps) {
  const [name, setName] = useState('');

  // Reset name when dialog opens
  React.useEffect(() => {
    if (open) {
      setName(''); // Start with empty string
    }
  }, [open]);

  const handleConfirm = () => {
    const finalName = name.trim() || defaultName; // Use default if empty
    onConfirm(finalName);
    onOpenChange(false);
    setName(''); // Reset for next use
  };

  const handleCancel = () => {
    onOpenChange(false);
    setName(''); // Reset
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[425px]"
        onKeyDown={(e) => {
          // Prevent spacebar from propagating to the viewport
          if (e.key === ' ') {
            e.stopPropagation();
          }
        }}
        onKeyUp={(e) => {
          // Also stop on key up
          if (e.key === ' ') {
            e.stopPropagation();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Name Your Group/Rack</DialogTitle>
          <DialogDescription>
            Enter a name for this group/rack to help organize your products.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">
              Name
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                // Stop propagation to prevent spacebar from triggering pan mode
                e.stopPropagation();
                
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirm();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handleCancel();
                }
              }}
              onKeyUp={(e) => {
                // Also stop propagation on key up
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                // Prevent any interference with text selection
                e.stopPropagation();
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
              }}
              className="col-span-3"
              style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
              placeholder="e.g., Equipment Rack 1"
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!name.trim()}>
            Create Group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}